'use client'

import { useState, useRef, useEffect, useCallback } from 'react'
import { Rnd } from 'react-rnd'
import ReactMarkdown from 'react-markdown'
import { MessageCircle, X, Send, Mic, Volume2, VolumeX, Maximize2, Minimize2, FileText, Upload, RotateCcw } from 'lucide-react'

interface Message {
  role: 'user' | 'ai'
  text: string
  actions?: string[]
  offerTicket?: boolean
  confidence?: 'high' | 'medium' | 'low'
}

interface ITSMChatWidgetProps {
  isOpen: boolean
  onClose: () => void
}

const DEFAULT_GREETING: Message = {
  role: 'ai',
  text: '¡Hola! Soy tu Agente de Soporte ITSM. ¿En qué puedo ayudarte hoy?',
  confidence: 'high'
}

const STORAGE_KEY = 'itsm-chat-history'
const VOICE_MUTE_KEY = 'itsm-voice-muted'
const SUGGESTED_QUESTIONS = ['VPN no conecta', 'SAP bloqueado', 'No recibo correo']

const loadMessagesFromStorage = (): Message[] => {
  if (typeof window === 'undefined') return []
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    return saved ? (JSON.parse(saved) as Message[]) : []
  } catch (e) {
    console.error('Error loading chat history:', e)
    return []
  }
}

const saveMessagesToStorage = (messages: Message[]): void => {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages))
  } catch (e) {
    console.error('Error saving chat history:', e)
  }
}

export default function ITSMChatWidget({ isOpen, onClose }: ITSMChatWidgetProps) {
  // STATE HOOKS
  const [isMounted, setIsMounted] = useState(false)
  const [messages, setMessages] = useState<Message[]>(() => {
    const loaded = loadMessagesFromStorage()
    return loaded.length > 0 ? loaded : [DEFAULT_GREETING]
  })
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [isMaximized, setIsMaximized] = useState(false)
  const [position, setPosition] = useState({ x: 100, y: 100 })
  const [size, setSize] = useState({ width: 450, height: 600 })
  const [uploading, setUploading] = useState(false)
  const [logoError, setLogoError] = useState(false)
  const [speechSupported, setSpeechSupported] = useState(true)

  // REF HOOKS
  const isMutedRef = useRef(false)
  const recognitionRef = useRef<any>(null)
  const synthRef = useRef<SpeechSynthesis | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // EFFECTS
  useEffect(() => { setIsMounted(true) }, [])

  useEffect(() => {
    if (!isMounted) return
    saveMessagesToStorage(messages)
  }, [messages, isMounted])

  useEffect(() => {
    if (!isMounted) return
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isMounted])

  useEffect(() => {
    if (!isMounted || typeof window === 'undefined') return

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    if (!SpeechRecognition) {
      setSpeechSupported(false)
      return
    }
    setSpeechSupported(true)

    recognitionRef.current = new SpeechRecognition()
    recognitionRef.current.continuous = true
    recognitionRef.current.interimResults = true
    recognitionRef.current.lang = 'es-MX'

    recognitionRef.current.onresult = (event: any) => {
      const transcript = Array.from(event.results).map((r: any) => r[0].transcript).join('')
      setInput(transcript)
    }

    // 🔥 MODO CONTINUO: Auto-restart listening after response
    recognitionRef.current.onend = () => {
      setIsRecording(false)
      if (isRecording && isMounted) {
        setTimeout(() => {
          try { recognitionRef.current?.start() } catch (e) {}
        }, 300)
      }
    }

    recognitionRef.current.onerror = (event: any) => {
      if (event.error !== 'no-speech' && event.error !== 'aborted') setIsRecording(false)
      if (event.error === 'no-speech' && isRecording) {
        setTimeout(() => { try { recognitionRef.current?.start() } catch (e) {} }, 500)
      }
    }

    synthRef.current = window.speechSynthesis
    const loadVoices = () => {
      if (!synthRef.current) return
      const voices = synthRef.current.getVoices()
      const mxVoice = voices.find((v: any) => v.lang === 'es-MX' || (v.lang.includes('es-') && v.name.toLowerCase().includes('mexico')))
      if (mxVoice) console.log('MX voice:', mxVoice.name)
    }
    loadVoices()
    if (speechSynthesis.onvoiceschanged !== undefined) speechSynthesis.onvoiceschanged = loadVoices

    const savedMute = localStorage.getItem(VOICE_MUTE_KEY)
    if (savedMute === 'true') { isMutedRef.current = true; setIsMuted(true) }

    return () => {
      if (recognitionRef.current) recognitionRef.current.stop()
      if (synthRef.current) synthRef.current.cancel()
    }
  }, [isMounted, isRecording])

  // CALLBACKS
  const removeEmojis = (text: string): string => {
    return text.replace(/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu, '')
  }

  const speak = useCallback((text: string) => {
    if (!synthRef.current || isMutedRef.current || !text.trim() || !isMounted) return
    synthRef.current.cancel()
    const sentences = text.split(/(?<=[.!?])\s+/).filter(s => s.trim().length > 0)
    let currentSentence = 0

    const speakNext = () => {
      if (isMutedRef.current || currentSentence >= sentences.length) { setIsSpeaking(false); return }
      const sentence = sentences[currentSentence].trim()
      if (!sentence) { currentSentence++; speakNext(); return }
      currentSentence++
      const cleanSentence = removeEmojis(sentence)
        const utterance = new SpeechSynthesisUtterance(cleanSentence)
      utterance.lang = 'es-MX'
      utterance.rate = 0.92
      utterance.pitch = 1.0
      utterance.volume = 1.0
      const voices = synthRef.current?.getVoices() || []
      const mxVoice = voices.find((v: any) => v.lang === 'es-MX') || voices.find((v: any) => v.lang.includes('es-') && v.name.toLowerCase().includes('google'))
      if (mxVoice) utterance.voice = mxVoice
      utterance.onstart = () => setIsSpeaking(true)
      utterance.onend = () => {
        if (!isMutedRef.current) setTimeout(speakNext, 150)
        else setIsSpeaking(false)
      }
      utterance.onerror = () => setIsSpeaking(false)
      synthRef.current?.speak(utterance)
    }
    speakNext()
  }, [isMounted])

  const toggleMute = useCallback(() => {
    const newState = !isMuted
    isMutedRef.current = newState
    setIsMuted(newState)
    if (newState && synthRef.current) { synthRef.current.cancel(); setIsSpeaking(false) }
    localStorage.setItem(VOICE_MUTE_KEY, String(newState))
  }, [isMuted])

  const startRecording = useCallback(() => {
    if (!speechSupported || !recognitionRef.current || !isMounted) {
      alert('Tu navegador no soporta reconocimiento de voz. Usa Chrome o Edge.')
      return
    }
    if (isSpeaking && synthRef.current) { synthRef.current.cancel(); setIsSpeaking(false) }
    setInput('')
    setIsRecording(true) // 🔥 Activa modo continuo
    try { recognitionRef.current.start() } catch (e) {}
  }, [speechSupported, isSpeaking, isMounted])

  const stopRecording = useCallback(() => {
    setIsRecording(false) // 🔥 Desactiva modo continuo
    if (recognitionRef.current) { try { recognitionRef.current.stop() } catch (e) {} }
  }, [])

  const sendToAI = useCallback(async (text: string) => {
    if (!text.trim() || !isMounted) return
    setIsLoading(true)
    setMessages(prev => [...prev, { role: 'user', text }])
    setInput('')

    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, context: { language: 'es-MX', region: 'Mexico' }, kb_context: SUGGESTED_QUESTIONS })
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const aiMessage: Message = {
        role: 'ai',
        text: data.reply || 'No pude procesar tu solicitud. Por favor, reformula tu pregunta.',
        actions: data.actions || [],
        offerTicket: data.offer_ticket || false,
        confidence: data.confidence || 'medium'
      }
      setMessages(prev => [...prev, aiMessage])

      // 🔥 MODO CONTINUO: Después de hablar, volver a escuchar si está activo
      if (!isMutedRef.current && data.reply) {
        await new Promise<void>((resolve) => {
          if (!synthRef.current) { resolve(); return }
          const utterance = new SpeechSynthesisUtterance(data.reply)
          utterance.lang = 'es-MX'
          utterance.rate = 0.92
          const voices = synthRef.current?.getVoices() || []
          const mxVoice = voices.find((v: any) => v.lang === 'es-MX')
          if (mxVoice) utterance.voice = mxVoice
          utterance.onstart = () => setIsSpeaking(true)
          utterance.onend = () => {
            setIsSpeaking(false)
            // 🔥 Auto-restart listening after speaking
            if (isRecording && speechSupported && recognitionRef.current) {
              setTimeout(() => { try { recognitionRef.current.start() } catch (e) {} }, 500)
            }
            resolve()
          }
          utterance.onerror = () => { setIsSpeaking(false); resolve() }
          synthRef.current?.speak(utterance)
        })
      }
    } catch (error) {
      console.error('Chat error:', error)
      setMessages(prev => [...prev, { role: 'ai', text: 'Hubo un error de conexión. Por favor, intenta de nuevo.', confidence: 'low' }])
    } finally {
      setIsLoading(false)
    }
  }, [isMounted, isRecording, speechSupported])

  const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !isMounted) return
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = await fetch('/api/upload-document', { method: 'POST', body: formData })
      if (res.ok) {
        const data = await res.json()
        setMessages(prev => [...prev, { role: 'ai', text: `✅ Listo. El documento "${file.name}" ya está cargado.\n\n📄 Fragmentos procesados: ${data.chunks || data.pages}\n🔍 Ahora puedo buscar información en este documento.`, confidence: 'high' }])
      } else { throw new Error('Upload failed') }
    } catch (error) {
      console.error('Upload error:', error)
      setMessages(prev => [...prev, { role: 'ai', text: '❌ No pude cargar el documento. Por favor, intenta de nuevo.', confidence: 'low' }])
    } finally {
      setUploading(false)
      if (event.target) event.target.value = ''
    }
  }, [isMounted])

  const createTicket = useCallback(async () => {
    if (!isMounted) return
    const lastUserMsg = messages.filter(m => m.role === 'user').pop()?.text || ''
    try {
      const res = await fetch('/api/ticket', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: 'Ticket Demo', description: lastUserMsg, user: 'Usuario Demo', priority: 'Medium' })
      })
      const data = await res.json()
      setMessages(prev => [...prev, { role: 'ai', text: `✅ Ticket #${data.ticket?.number || 'DEMO-001'} creado.`, confidence: 'high' }])
    } catch (e) {
      console.error('Ticket error:', e)
      setMessages(prev => [...prev, { role: 'ai', text: 'Error al crear ticket. Por favor, intenta de nuevo.', confidence: 'low' }])
    }
  }, [messages, isMounted])

  const clearChat = useCallback(() => {
    if (!isMounted) return
    if (confirm('¿Borrar todo el historial de este chat?')) {
      localStorage.removeItem(STORAGE_KEY)
      setMessages([DEFAULT_GREETING])
    }
  }, [isMounted])

  // EARLY RETURN (DESPUÉS DE TODOS LOS HOOKS)
  if (!isMounted || !isOpen) return null

  // RENDER
  return (
    <Rnd size={isMaximized ? { width: '100vw', height: '100vh' } : size} position={isMaximized ? { x: 0, y: 0 } : position}
      onDragStop={(e, d) => { if (!isMaximized) setPosition({ x: d.x, y: d.y }) }}
      onResizeStop={(e, direction, ref, delta, pos) => { if (!isMaximized) { setSize({ width: parseInt(ref.style.width), height: parseInt(ref.style.height) }); setPosition(pos) } }}
      minWidth={350} minHeight={400} bounds="window" dragHandleClassName="chat-drag-handle" disableDragging={isMaximized} enableResizing={!isMaximized} className="z-[9998]">
      <div className="bg-white w-full h-full flex flex-col overflow-hidden rounded-xl shadow-2xl border border-gray-200">
        
        {/* HEADER */}
        <div className="chat-drag-handle bg-blue-600 text-white p-3 flex items-center justify-between cursor-move select-none">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {!logoError ? (
              <div className="flex-shrink-0 bg-white rounded p-1">
                <img src="/logos/logo.png" alt="Logo FG" className="h-8 w-auto object-contain block" onError={() => setLogoError(true)} />
              </div>
            ) : (
              <div className="flex-shrink-0 w-10 h-10 bg-white/20 rounded-lg p-2 flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
            )}
            <div className="min-w-0">
              <h3 className="font-semibold text-sm truncate">Soporte FG 🇲🇽</h3>
              <p className="text-xs text-blue-100 flex items-center gap-1">
                <span className={`w-2 h-2 rounded-full animate-pulse ${isSpeaking && !isMuted ? 'bg-green-400' : isRecording ? 'bg-red-400' : 'bg-gray-400'}`}></span>
                {isSpeaking && !isMuted ? 'Hablando...' : isRecording ? '🎤 Escuchando...' : 'En línea'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={clearChat} className="hover:bg-white/20 p-1.5 rounded transition" title="Borrar historial"><RotateCcw className="w-4 h-4" /></button>
            <button onClick={toggleMute} className={`p-1.5 rounded transition ${isMuted ? 'bg-red-500/30 hover:bg-red-500/50' : 'hover:bg-white/20'}`} title={isMuted ? 'Activar voz' : 'Silenciar voz'}>
              {isMuted ? <VolumeX className="w-5 h-5 text-red-300" /> : <Volume2 className="w-5 h-5" />}
            </button>
            <button onClick={() => setIsMaximized(!isMaximized)} className="hover:bg-white/20 p-1.5 rounded transition" title={isMaximized ? 'Restaurar' : 'Pantalla completa'}>
              {isMaximized ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            <button onClick={onClose} className="hover:bg-white/20 p-1.5 rounded transition" title="Cerrar"><X className="w-5 h-5" /></button>
          </div>
        </div>
        
        {isMuted && (
          <div className="bg-red-50 border-b border-red-200 px-4 py-2 flex items-center gap-2 text-red-700 text-xs">
            <VolumeX className="w-4 h-4" /><span>Voz silenciada. El agente responde solo por texto.</span>
            <button onClick={toggleMute} className="ml-auto text-red-600 hover:text-red-800 font-medium">Activar voz</button>
          </div>
        )}
        
        {!speechSupported && (
          <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2 flex items-center gap-2 text-yellow-800 text-xs">
            <MessageCircle className="w-4 h-4" /><span>Modo texto: tu navegador no soporta voz. Usa Chrome para activarla.</span>
          </div>
        )}
        
        {/* MESSAGES */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-xl px-4 py-3 ${msg.role === 'user' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200'}`}>
                <div className="text-sm prose prose-sm max-w-none">
                  <ReactMarkdown components={{
                    strong: ({node, ...props}) => <strong className="font-bold" {...props} />,
                    em: ({node, ...props}) => <em className="italic" {...props} />,
                    p: ({node, ...props}) => <p className="mb-2 last:mb-0" {...props} />,
                    ol: ({node, ...props}) => <ol className="list-decimal list-inside ml-4 mb-2" {...props} />,
                    ul: ({node, ...props}) => <ul className="list-disc list-inside ml-4 mb-2" {...props} />,
                    li: ({node, ...props}) => <li className="mb-1" {...props} />,
                  }}>{msg.text}</ReactMarkdown>
                </div>
                {msg.actions && msg.actions.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {msg.actions.map((action, idx) => (
                      <button key={idx} onClick={() => sendToAI(action)} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded hover:bg-gray-200">{action}</button>
                    ))}
                  </div>
                )}
                {msg.offerTicket && (
                  <button onClick={createTicket} className="mt-2 w-full bg-indigo-50 text-indigo-700 text-xs py-2 rounded flex items-center justify-center gap-1 hover:bg-indigo-100 transition">
                    <FileText className="w-3 h-3" /> Crear Ticket
                  </button>
                )}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
                <div className="flex items-center gap-2 text-gray-500 text-xs">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                  <span>Agente pensando...</span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        
        {/* INPUT */}
        <div className="p-4 bg-white border-t border-gray-200 rounded-b-2xl">
          <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.txt" onChange={handleFileUpload} className="hidden" />
          <div className="flex gap-2 items-center mb-3">
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm disabled:opacity-50">
              <Upload className="w-4 h-4" /> {uploading ? 'Cargando...' : '📤 Cargar PDF/DOCX'}
            </button>
          </div>
          <div className="flex gap-2 items-center">
            <button onMouseDown={startRecording} onMouseUp={stopRecording} onTouchStart={startRecording} onTouchEnd={stopRecording}
              className={`p-3 rounded-xl transition-all ${isRecording ? 'bg-red-500 text-white animate-pulse scale-110 shadow-lg' : speechSupported ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
              title={isRecording ? 'Suelta para enviar' : speechSupported ? 'Mantén presionado para hablar' : 'Voz no soportada'} disabled={!speechSupported}>
              <Mic className="w-5 h-5" />
            </button>
            {isSpeaking && !isMuted && (
              <div className="flex items-center gap-1 text-blue-600 bg-blue-50 px-3 py-2 rounded-lg">
                <Volume2 className="w-5 h-5 animate-pulse" /><span className="text-xs font-medium">Escuchando respuesta...</span>
              </div>
            )}
            {isMuted && (
              <div className="flex items-center gap-1 text-red-600 bg-red-50 px-3 py-2 rounded-lg">
                <VolumeX className="w-5 h-5" /><span className="text-xs font-medium">Voz silenciada</span>
              </div>
            )}
            <input type="text" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && sendToAI(input)}
              placeholder={isRecording ? '🎤 Escuchando... suelta para enviar' : speechSupported ? 'Describe tu problema...' : 'Escribe tu pregunta...'}
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50" disabled={isLoading || isRecording} />
            <button onClick={() => sendToAI(input)} disabled={!input.trim() || isLoading} className="bg-blue-600 text-white p-2 rounded-lg disabled:opacity-50 hover:bg-blue-700 transition" title="Enviar mensaje">
              <Send className="w-5 h-5" />
            </button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {SUGGESTED_QUESTIONS.map((q) => (
              <button key={q} onClick={() => sendToAI(q)} className="text-xs bg-gray-100 px-3 py-1.5 rounded-full hover:bg-gray-200 transition border border-gray-200" disabled={isLoading}>{q}</button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2 text-center">
            {speechSupported ? '💡 Mantén presionado 🎤 para hablar, suelta para enviar' : '💡 Usa Chrome o Edge para activar reconocimiento de voz'}
          </p>
        </div>
      </div>
    </Rnd>
  )
}
